/*
 * CMS Text — tab strip scripts (self-contained; the section loads only this file).
 *
 * 1. Sticky header offset: publishes --sticky-header-offset on <html> so the
 *    desktop sticky tab column sits below the revealed header on scroll-up.
 *    Shares the [data-sticky-header-offset-init] singleton guard with
 *    product-row-sticky-offset.js, so only one publisher runs per page.
 *    Hooks: [data-header] (height), [data-header-group] [data-header-sticky] (observed).
 *
 * 2. Active-tab reveal: on mobile, scrolls the active tab towards the left of the
 *    horizontal strip on load so it is visible without the user scrolling.
 *    Horizontal only — it sets scrollLeft, never the page scroll; re-runs once web
 *    fonts settle so the measurement stays accurate. No-op on desktop. Idempotent
 *    via [data-cms-text-tab-scroll-init] on <html>.
 *    Hooks: [data-cms-text-tabs-mobile] (read), [aria-current="page"] (read).
 */
(function () {
  'use strict';

  // 1. Sticky header offset publisher — shared singleton with product-row-sticky-offset.js.
  (function () {
    if (document.documentElement.dataset.stickyHeaderOffsetInit === 'true') return;

    const header = document.querySelector('[data-header]');
    if (!header) return;
    // Sticky state lives on the header GROUP (bar + header), not [data-header].
    const group = document.querySelector('[data-header-group]') || header;

    document.documentElement.dataset.stickyHeaderOffsetInit = 'true';

    function updateOffset() {
      const isVisible = group.dataset.headerSticky === 'visible';
      document.documentElement.style.setProperty(
        '--sticky-header-offset',
        isVisible ? header.offsetHeight + 'px' : '0px'
      );
    }

    updateOffset();

    new MutationObserver(updateOffset).observe(group, {
      attributes: true,
      attributeFilter: ['data-header-sticky'],
    });

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(updateOffset).observe(header);
    }
  })();

  // 2. Active-tab reveal on mobile.
  (function () {
    if (document.documentElement.dataset.cmsTextTabScrollInit === 'true') return;
    document.documentElement.dataset.cmsTextTabScrollInit = 'true';

    function revealActiveTabs() {
      // Read spacing tokens from the theme like the other scripts (e.g. 50-50-large-promos-sticky.js).
      // `lead` matches the strip's leading padding (px-md); a tab that is neither the first nor the
      // last gets an extra 2xl offset so the previous tab peeks into view on its left.
      const root = getComputedStyle(document.documentElement);
      const lead = parseFloat(root.getPropertyValue('--spacing-md')) || 16;
      const peek = parseFloat(root.getPropertyValue('--spacing-2xl')) || 32;

      document.querySelectorAll('[data-cms-text-tabs-mobile]').forEach(function (list) {
        const active = list.querySelector('[aria-current="page"]');
        const nav = list.closest('nav');
        if (!active || !nav) return;

        const item = active.closest('li');
        const isEdge = !item || item === list.firstElementChild || item === list.lastElementChild;
        const offset = isEdge ? lead : lead + peek;

        const navRect = nav.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        const target = nav.scrollLeft + (activeRect.left - navRect.left) - offset;

        nav.scrollLeft = Math.max(0, target);
      });
    }

    function run() {
      revealActiveTabs();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(revealActiveTabs);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  })();
})();
